import { useState } from 'react'
import {
  Zap,
  Activity,
  Volume2,
  ArrowUpRight,
  RotateCcw,
  Share2,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Sparkles,
} from 'lucide-react'
import type { ArtifactReport } from '../lib/analyzer'
import { STREAMING_TARGETS } from '../lib/analyzer'
import { ScoreCircle } from './ScoreCircle'
import { ArtifactMeter } from './ArtifactMeter'
import { WaveformDisplay } from './WaveformDisplay'
import {
  formatDuration,
  getSizzleSeverity,
  getMuddinessSeverity,
  getClickSeverity,
  getDynamicRangeSeverity,
  getLufsSeverity,
} from '../utils/scoring'
import { getOverallSummary, getQuickFixSummary } from '../utils/recommendations'

interface QualityReportProps {
  report: ArtifactReport
  file: File
  onScanAnother: () => void
  onShare: () => void
}

export function QualityReport({ report, file, onScanAnother, onShare }: QualityReportProps) {
  const [showDetails, setShowDetails] = useState(true)

  const sizzleSeverity = getSizzleSeverity(report.sizzleScore)
  const muddinessSeverity = getMuddinessSeverity(report.muddinessScore)
  const clickSeverity = getClickSeverity(report.clickCount)
  const drSeverity = getDynamicRangeSeverity(report.dynamicRangeDb)
  const lufsSeverity = getLufsSeverity(report.lufs)

  const quickFixes = getQuickFixSummary(report)
  const summary = getOverallSummary(report.overallScore, report)

  const musicToUrl = 'https://luminary.tools?utm_source=scanner&utm_medium=cta'

  const getLufsStatusClass = (diff: number) => {
    if (Math.abs(diff) <= 1) return 'text-forge-success'
    if (diff > 0) return 'text-forge-copper'
    return 'text-forge-muted'
  }

  return (
    <div className="w-full max-w-3xl mx-auto space-y-6">
      {/* Header with score */}
      <div className="forge-card p-6">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <ScoreCircle score={report.overallScore} />

          <div className="flex-1 text-center md:text-left">
            <h2 className="text-xl font-semibold text-white mb-2">
              AI Audio Quality Report
            </h2>
            <div className="flex items-center justify-center md:justify-start gap-3 text-sm text-forge-muted mb-3">
              <span className="font-medium truncate max-w-[200px]">{file.name}</span>
              <span className="text-forge-subtle">•</span>
              <span className="font-mono">{formatDuration(report.duration)}</span>
            </div>
            {report.detectedSource && (
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-forge-surface rounded-full text-xs mb-3">
                <span className="text-forge-subtle">Detected:</span>
                <span className="text-forge-copper font-medium">{report.detectedSource}</span>
              </div>
            )}
            <p className="text-sm text-forge-muted leading-relaxed">{summary}</p>
          </div>
        </div>
      </div>

      {/* Artifact Analysis */}
      <div className="forge-card overflow-hidden">
        <button
          className="w-full flex items-center justify-between p-4 hover:bg-[hsl(var(--forge-bg-surface)/0.5)] transition-colors"
          onClick={() => setShowDetails(!showDetails)}
        >
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-forge-copper" />
            Artifact Analysis
          </h3>
          {showDetails ? (
            <ChevronUp className="w-5 h-5 text-forge-muted" />
          ) : (
            <ChevronDown className="w-5 h-5 text-forge-muted" />
          )}
        </button>

        {showDetails && (
          <div className="p-4 pt-0 space-y-3">
            <ArtifactMeter
              label="Sizzle (high-freq harshness)"
              value={report.sizzleScore}
              inverted
              severity={sizzleSeverity.severity}
              description="Energy ratio in 5-8kHz band. Common AI artifact."
            />

            <ArtifactMeter
              label="Muddiness (low-mid buildup)"
              value={report.muddinessScore}
              inverted
              severity={muddinessSeverity.severity}
              description="Sub-bass energy relative to bass clarity."
            />

            <ArtifactMeter
              label="Clicks & Pops"
              value={report.clickCount}
              min={0}
              max={25}
              inverted
              severity={clickSeverity.severity}
              description={
                report.clickCount > 0
                  ? `Transient artifacts detected at ${report.clickPositions.slice(0, 3).map((p) => formatDuration(p)).join(', ')}${report.clickPositions.length > 3 ? '...' : ''}`
                  : 'No transient artifacts detected.'
              }
            />

            <ArtifactMeter
              label="Dynamic Range"
              value={report.dynamicRangeDb}
              min={0}
              max={20}
              unit="dB"
              severity={drSeverity.severity}
              description="Peak-to-RMS ratio. Lower values indicate over-compression."
            />
          </div>
        )}
      </div>

      {/* Loudness */}
      <div className="forge-card p-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <Volume2 className="w-5 h-5 text-forge-copper" />
          Loudness Analysis
        </h3>

        <div className="flex items-center justify-between mb-5 p-4 bg-forge-surface rounded-xl">
          <div>
            <p className="text-xs text-forge-subtle uppercase tracking-wide mb-1">Current Loudness</p>
            <p className={`text-3xl font-bold font-mono ${lufsSeverity.color === 'text-emerald-400' ? 'text-forge-success' : lufsSeverity.color === 'text-orange-400' ? 'text-forge-copper' : 'text-forge-warning'}`}>
              {report.lufs.toFixed(1)}
              <span className="text-lg text-forge-muted ml-1">LUFS</span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-forge-subtle uppercase tracking-wide mb-1">Status</p>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              lufsSeverity.severity === 'good'
                ? 'bg-[hsl(var(--forge-success)/0.15)] text-forge-success'
                : lufsSeverity.severity === 'moderate'
                  ? 'bg-[hsl(var(--forge-warning)/0.15)] text-forge-warning'
                  : 'bg-[hsl(var(--forge-copper)/0.15)] text-forge-copper'
            }`}>
              {lufsSeverity.label}
            </span>
          </div>
        </div>

        <div className="space-y-1">
          {STREAMING_TARGETS.map((target) => {
            const diff = report.lufs - target.lufs
            const statusClass = getLufsStatusClass(diff)

            return (
              <div
                key={target.name}
                className="flex items-center justify-between text-sm py-2.5 px-3 rounded-lg hover:bg-forge-surface transition-colors"
              >
                <span className="text-forge-muted">{target.name}</span>
                <div className="flex items-center gap-4">
                  <span className="text-forge-subtle font-mono">{target.lufs} LUFS</span>
                  <span className={`font-medium ${statusClass} min-w-[80px] text-right`}>
                    {Math.abs(diff) <= 1
                      ? '✓ Good'
                      : diff > 0
                        ? `↓ ${diff.toFixed(1)} dB`
                        : `↑ ${Math.abs(diff).toFixed(1)} dB`}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Waveform */}
      <WaveformDisplay
        file={file}
        clickPositions={report.clickPositions}
        duration={report.duration}
      />

      {/* Recommendations */}
      {report.recommendations.length > 0 && (
        <div className="forge-card p-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
            <Lightbulb className="w-5 h-5 text-forge-warning" />
            Recommendations
          </h3>

          <ul className="space-y-3">
            {report.recommendations.map((rec, i) => (
              <li key={i} className="flex gap-3 text-sm">
                <span className="flex-shrink-0 w-6 h-6 rounded-lg bg-forge-surface flex items-center justify-center text-forge-copper font-mono text-xs">
                  {i + 1}
                </span>
                <span className="text-forge-muted leading-relaxed">{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* CTA Section */}
      <div className="relative overflow-hidden rounded-2xl">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--forge-copper)/0.15)] via-[hsl(var(--forge-bg-elevated))] to-[hsl(var(--forge-copper)/0.05)]" />
        <div className="absolute inset-0 forge-grid-bg opacity-20" />

        <div className="relative p-6 border border-[hsl(var(--forge-copper)/0.3)] rounded-2xl">
          <div className="text-center mb-5">
            <div className="inline-flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-forge-copper" />
              <h3 className="text-xl font-semibold text-white">
                Fix these issues automatically
              </h3>
            </div>
            <p className="text-sm text-forge-muted">
              Forge's AI mastering can fix all detected artifacts in seconds.
            </p>
          </div>

          {quickFixes.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 mb-5">
              {quickFixes.map((fix, i) => (
                <span
                  key={i}
                  className="px-3 py-1.5 bg-forge-surface border border-forge rounded-full text-xs text-white font-medium"
                >
                  {fix}
                </span>
              ))}
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={musicToUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="forge-btn-primary"
            >
              <Zap className="w-4 h-4" />
              Fix with Forge Mastering
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Secondary actions */}
      <div className="flex flex-wrap items-center justify-center gap-3">
        <button
          onClick={onShare}
          className="forge-btn-secondary"
        >
          <Share2 className="w-4 h-4" />
          Share Report
        </button>

        <button
          onClick={onScanAnother}
          className="forge-btn-secondary"
        >
          <RotateCcw className="w-4 h-4" />
          Scan Another
        </button>
      </div>
    </div>
  )
}
